"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Mail, User, MessageSquare, Send, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
function ContactForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section ref={ref} className="relative min-h-screen flex items-center justify-center py-16 px-4 overflow-hidden bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10">
            {}
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-secondary/15 pointer-events-none" />

            <div className={`relative w-full max-w-lg transition-all duration-[var(--duration-medium)] ease-[var(--ease-scroll)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <FormProvider className="glass-effect p-8 shadow-strong" formId="contact-form-main" sectionName="contact-section" defaultValues={{
        name: "",
        email: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Message" successMessage="Thank you! Your message has been sent successfully." errorMessage="Failed to send message. Please try again.">
                    <ContactFormContent />
                </FormProvider>
            </div>
        </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const sendEmail = async (formData: {
    name: string;
    email: string;
    message: string;
  }) => {
    try {
      setIsSendingEmail(true);
      setEmailError(null);
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: 'mario.cavalloni@pm.me',
          replyTo: formData.email,
          from: formData.email,
          senderName: formData.name,
          subject: `New Contact Form Message from ${formData.name}`,
          message: formData.message,
          timestamp: new Date().toISOString()
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send email');
      }
      return {
        success: true
      };
    } catch (error) {
      console.error('Email sending error:', error);
      setEmailError(error instanceof Error ? error.message : 'Failed to send email. Please try again.');
      return {
        success: false,
        error
      };
    } finally {
      setIsSendingEmail(false);
    }
  };
  const handleFormSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    const formValues = control._formValues;
    const emailResult = await sendEmail(formValues);
    if (emailResult.success) {
      await handleSubmit(e);
    }
  };
  if (isSubmitted && !submitError && !emailError) {
    return <div className="text-center space-y-6 animate-fade-in-up">
                <div className="flex justify-center">
                    <CircleCheck className="size-16 text-foreground animate-pulse" />
                </div>
                <div className="space-y-2">
                    <h3 className="text-2xl font-display font-bold text-foreground">Message Sent!</h3>
                    <p className="text-muted-foreground">Contattaci pure per qualsiasi dubbio o richiesta di preventivo, saremo lieti di rispondere il prima possibile.</p>
                    <p className="text-sm text-muted-foreground">We've sent your message to mario.cavalloni@pm.me</p>
                </div>
                <Button onClick={resetForm} variant="outline" className="hover:scale-[1.03] active:scale-[0.97] transition-transform duration-[var(--duration-fast)] ease-[var(--ease-spring)]">
                    Send Another Message
                </Button>
            </div>;
  }
  return <div className="space-y-8">
            <div className="text-center space-y-2 animate-fade-in-up">
                <h2 className="text-3xl font-display font-bold text-foreground">Contattaci</h2>
                <p className="text-muted-foreground">
                    We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                </p>
            </div>

            <Form onSubmit={handleFormSubmit} className="space-y-6"><div data-slot="form-item" className="grid gap-2 animate-fade-in-up" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:lciy11" data-ast-start="5567" data-ast-end="6438"><label data-slot="form-label" className="text-sm leading-none select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive flex items-center gap-2 text-foreground font-semibold" data-error="false" for="_r_g_-form-item" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:wi95bq" data-ast-start="5637" data-ast-end="6211"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-user size-4 text-foreground" aria-hidden="true" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:t2gzf0" data-ast-start="5714" data-ast-end="6184"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:apk6su" data-ast-start="6081" data-ast-end="6140"></path><circle cx="12" cy="7" r="4" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:5ntalz" data-ast-start="6140" data-ast-end="6178"></circle></svg>Mario Cavalloni</label></div><div data-slot="form-item" className="grid gap-2 animate-fade-in-up-delay-100" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:o6adcx" data-ast-start="6728" data-ast-end="7856"><label data-slot="form-label" className="text-sm leading-none select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive flex items-center gap-2 text-foreground font-semibold" data-error="false" for="_r_h_-form-item" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:x0cgor" data-ast-start="6808" data-ast-end="7629"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-mail size-4 text-foreground" aria-hidden="true" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:xecrds" data-ast-start="6885" data-ast-end="7596"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:b7ni5v" data-ast-start="7252" data-ast-end="7422"></path><rect x="2" y="4" width="20" height="16" rx="2" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:5crr3d" data-ast-start="7422" data-ast-end="7590"></rect></svg>mario.cavalloni@pm.me</label></div><div data-slot="form-item" className="grid gap-2 animate-fade-in-up-delay-200" data-ast-id="f:/src/pages/Contact/sections/ContactForm.tsx|sig:r9p28" data-ast-start="8130" data-ast-end="8437"></div></Form>

            <div className="text-center pt-4 border-t border-border/50">
                <p className="text-xs text-muted-foreground">Grazie, il team di CiakMediaLab</p>
            </div>
        </div>;
}
export default ContactForm;